import { FC, useState, useRef } from 'react';
import Form from 'antd/lib/form';
import ReCAPTCHA from 'react-google-recaptcha';
import { Select } from 'antd';
import Uploader from '@elem/compCommon/Uploader';
import styled from './index.module.scss';
import { Option } from './Config';
import { message } from '@/components/UI/Message';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/getLocal';
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Input';
import { ENGLISH_LANG, rpcMap } from '@/constants/lang';
import { euEmailSource, EU_SITES } from '@/constants/membersday';
import { createTradeIn, subscribe } from '@/service/membersday';
import { getPrivacyPolicy } from '@/utils/common';

interface IProps {
    step: number;
    handleNext: (s: number) => void;
    handleGetCode: (code: string) => void;
    title?: string;
}

const MAX_FILESIZE = 2 * 1024 * 1024;

const TradeInForm: FC<IProps> = ({ step, handleNext, handleGetCode, title }) => {
    const { t } = useLang('membersday.return.tradein');
    const { locale } = getLocal();
    const SelectOption = Option[locale].map((item) => ({ label: item, value: item }));
    const [form] = Form.useForm();
    form.validateFields();

    const recaptchaRef = useRef(null);
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;

    const [form2] = Form.useForm();
    form2.validateFields();

    const [form1, setForm1] = useState({
        firstName: '',
        lastName: '',
        email: '',
        countryCode: '',
        recaptchaResponse: '',
    });

    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) {
            return;
        }
        setRecaptchaResponse(captchaCode);
    };

    const handleFinish = (values: any) => {
        setForm1(values);
        handleNext(step + 1);
    };

    const handleSubmit = async (values: any) => {
        const url = values?.pictureUrl?.[0];
        if (!url) return message.error('提交失败');
        const params = { ...values, ...form1, pictureUrl: url };
        createTradeIn(params, locale)
            .then(async (res) => {
                if (res.code !== '0') return message.error(res.message);
                // eu自动订阅
                if (EU_SITES.includes(locale)) {
                    try {
                        await subscribe(form1.email, locale, euEmailSource.tradeIn, {
                            sector_name: title || 'MembersDay TradeIn',
                            entry_position: 3,
                        });
                    } catch (err) {
                        console.log(err);
                    }
                }

                handleGetCode(res.data);
                handleNext(step + 1);
            })
            .catch((err) => console.log(err));
    };

    const handleBack = () => {
        setRecaptchaResponse('');
        handleNext(step - 1);
    };

    return (
        <div className={`${styled.Form} Form`}>
            {step === 1 && (
                <Form form={form} onFinish={handleFinish}>
                    <div className="row">
                        <Form.Item name="firstName" rules={[{ required: true }]}>
                            <Input placeholder={t('first_name')} maxLength={50} />
                        </Form.Item>
                        <Form.Item name="lastName">
                            <Input placeholder={t('last_name')} maxLength={50} />
                        </Form.Item>
                    </div>
                    <div className="row ">
                        <Form.Item className="item-email" name="email" rules={[{ required: true, type: 'email' }]}>
                            <Input placeholder={t('email')} maxLength={50} />
                        </Form.Item>
                        <p className="form_tips h5">{t('form_tips')}</p>
                        <Form.Item name="countryCode" rules={[{ required: true }]}>
                            <Select
                                placeholder={t('country')}
                                options={SelectOption}
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                            />
                        </Form.Item>
                    </div>
                    <p className="form_tips pc">{t('form_tips')}</p>
                    <p className="form_protocol">
                        {t.rich('form_protocol', {
                            line1: (text: string) => (
                                <a className="link" href={getPrivacyPolicy(locale)} target="_blank" rel="noreferrer">
                                    {text}
                                </a>
                            ),
                            line2: (text: string) => (
                                <a className="link" href={`https://${locale}.ecoflow.com/policies/terms-of-service`} target="_blank" rel="noreferrer">
                                    {text}
                                </a>
                            ),
                        })}
                    </p>
                    <div className="tools">
                        <Form.Item shouldUpdate>
                            {() => {
                                return (
                                    <Button className="btn" htmlType="submit" disabled={!!form.getFieldsError().filter(({ errors }) => errors.length).length}>
                                        {t('btn_next')}
                                    </Button>
                                );
                            }}
                        </Form.Item>
                    </div>
                </Form>
            )}
            {step === 2 && (
                <Form form={form2} onFinish={handleSubmit}>
                    <div className="row">
                        <Form.Item name="brand" rules={[{ required: true }]}>
                            <Input placeholder={t('product')} maxLength={100} />
                        </Form.Item>
                    </div>
                    <div className="row">
                        <Form.Item name="sn" rules={[{ required: true }]}>
                            <Input placeholder={t('sn')} maxLength={50} />
                        </Form.Item>
                        <Form.Item
                            name="pictureUrl"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value?.length ? Promise.resolve() : Promise.reject(new Error(t('upload_btn') || 'required')),
                                },
                            ]}
                            extra={
                                <>
                                    <p>{t('tip_line1')}</p>
                                    <p>{t('tip_line2')}</p>
                                </>
                            }
                            style={{ position: 'relative' }}
                        >
                            <Uploader
                                maxCount={1}
                                size={MAX_FILESIZE}
                                types={['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'jpg', 'jpeg', 'png', 'gif', 'webp']}
                                accept="image/*"
                                onChange={() => undefined}
                            />
                        </Form.Item>
                    </div>
                    <div className="row last">
                        <Form.Item name="upgrade" rules={[{ required: true }]}>
                            <Input placeholder={t('upgrade')} maxLength={500} />
                        </Form.Item>
                    </div>
                    <div className="row recaptcha">
                        <Form.Item name="recaptchaResponse" rules={[{ required: true }]}>
                            {/* @ts-ignore */}
                            <ReCAPTCHA theme="dark" ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                        </Form.Item>
                    </div>
                    <div className="tools">
                        <Button className="btn back" onClick={handleBack}>
                            {t('btn_back')}
                        </Button>
                        <Form.Item shouldUpdate>
                            {() => (
                                <Button
                                    className="btn"
                                    htmlType="submit"
                                    disabled={!!form2.getFieldsError().filter(({ errors }) => errors.length).length || recaptchaResponse === ''}
                                >
                                    {t('btn_next')}
                                </Button>
                            )}
                        </Form.Item>
                    </div>
                </Form>
            )}
        </div>
    );
};

export default TradeInForm;
